import { PROTECTED_ROUTES, findProtectedRoute } from "@/app/router/route-manifest";
import { matchesRouterPath } from "@/app/router/router-path";
import type { MemberStanding, ModuleId } from "@/types/clm-web";

/**
 * Restricted shell (006-6 FR-050, standing contract §6.1).
 *
 * A `pre_activation` member sees only what this file allows: the `dashboard`
 * module (`PRE_ACTIVATION_VISIBLE_MODULE_IDS`) and the member invoices route
 * plus its children (`MEMBER_INVOICES_ROUTE` via `isInvoicesPath`). Visibility
 * is an allow-list: any other module or route is hidden for that standing, so
 * new modules fail closed. The contract also reserves renewal and support
 * surfaces for this standing; they join the allow-list here once they exist.
 *
 * `renewal_inactive` and `blocked` deliberately keep the normal shell until
 * 006-6 US5 defines their surfaces.
 */
const PRE_ACTIVATION_VISIBLE_MODULE_IDS: readonly ModuleId[] = ["dashboard"];

const PAY_INVOICES_ROUTE = PROTECTED_ROUTES.find((route) => route.id === "pay-invoices");

if (!PAY_INVOICES_ROUTE) {
  throw new Error("Route manifest is missing the pay-invoices route.");
}

export const MEMBER_INVOICES_ROUTE = PAY_INVOICES_ROUTE;

export function isRestrictedStanding(standing: MemberStanding | undefined): boolean {
  return standing === "pre_activation";
}

export function isModuleVisibleForStanding(
  moduleId: ModuleId,
  standing: MemberStanding | undefined,
): boolean {
  return !isRestrictedStanding(standing) || PRE_ACTIVATION_VISIBLE_MODULE_IDS.includes(moduleId);
}

export function isInvoicesPath(pathname: string): boolean {
  return matchesRouterPath(`${MEMBER_INVOICES_ROUTE.path}/*`, pathname);
}

/**
 * True when the shell may render the routed surface for this pathname.
 * Unknown paths stay visible so the router's own fallback (redirect to the
 * dashboard) keeps working instead of dead-ending in the restricted message.
 */
export function isPathVisibleForStanding(
  pathname: string,
  standing: MemberStanding | undefined,
): boolean {
  if (!isRestrictedStanding(standing) || isInvoicesPath(pathname)) {
    return true;
  }

  const route = findProtectedRoute(pathname);
  if (!route?.moduleId) {
    return true;
  }

  return isModuleVisibleForStanding(route.moduleId, standing);
}
